import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User,
  ChangePasswordRequest,
  PaginatedResponse,
  ChangelogEntry,
  CustomerDashboard,
  AdminDashboard,
  ProviderDashboard,
  GeofenceSettings,
  CreateGeofenceSettingsInput,
  EmailDomainValidation,
} from './types'
import { createLogger } from '@/utils/logger'
import * as Sentry from '@sentry/vue'

// Interceptor types
type RequestInterceptor = (
  config: RequestConfig
) => RequestConfig | Promise<RequestConfig>
type ResponseInterceptor<T = unknown> = (
  response: Response,
  data: T
) => T | Promise<T>
type ErrorInterceptor = (error: ApiClientError) => never | Promise<never>

interface RequestConfig {
  url: string
  options: RequestInit
}

// Normalized error class
export class ApiClientError extends Error {
  public statusCode?: number
  public response?: Response
  public data?: unknown
  public isNetworkError: boolean = false
  public isRetryable: boolean = false

  constructor(
    message: string,
    options?: {
      statusCode?: number
      response?: Response
      data?: unknown
      isNetworkError?: boolean
      isRetryable?: boolean
    }
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.statusCode = options?.statusCode
    this.response = options?.response
    this.data = options?.data
    this.isNetworkError = options?.isNetworkError || false
    this.isRetryable = options?.isRetryable || false
  }
}

export class ApiClient {
  private baseUrl: string
  private token: string | null = null
  private logger = createLogger('ApiClient')

  // Interceptor storage
  private requestInterceptors: RequestInterceptor[] = []
  private responseInterceptors: ResponseInterceptor[] = []
  private errorInterceptors: ErrorInterceptor[] = []

  // Retry configuration
  private maxRetries: number = 3
  private retryDelay: number = 1000
  private retryableStatusCodes: Set<number> = new Set([
    408, 429, 500, 502, 503, 504,
  ])

  constructor(baseUrl?: string) {
    // Use provided baseUrl, or VITE_API_BASE_URL from env
    // If VITE_API_BASE_URL is empty string, use '' (relative URLs for nginx proxy)
    // Only fall back to localhost if VITE_API_BASE_URL is undefined
    const envUrl = import.meta.env.VITE_API_BASE_URL
    this.baseUrl =
      baseUrl ?? (envUrl !== undefined ? envUrl : 'http://localhost:8080')

    this.loadToken()
    this.setupDefaultInterceptors()
  }

  // Setup default interceptors
  private setupDefaultInterceptors(): void {
    // Request logging and Sentry breadcrumb interceptor
    this.addRequestInterceptor(config => {
      const method = config.options.method || 'GET'
      this.logger.log(`${method} ${config.url}`)

      // Add Sentry breadcrumb for API request
      Sentry.addBreadcrumb({
        category: 'http',
        message: `${method} ${config.url}`,
        level: 'info',
        data: {
          method,
          url: config.url,
        },
      })

      return config
    })

    // Response logging and Sentry breadcrumb interceptor
    this.addResponseInterceptor((response, data) => {
      this.logger.log(`Response ${response.status} from ${response.url}`)

      // Add Sentry breadcrumb for successful response
      Sentry.addBreadcrumb({
        category: 'http',
        message: `HTTP ${response.status} ${response.url}`,
        level: 'info',
        data: {
          status: response.status,
          url: response.url,
        },
      })

      return data
    })

    // Global 401 error handler - must be first to catch auth failures
    this.addErrorInterceptor(error => {
      if (error.statusCode === 401) {
        this.logger.warn('401 Unauthorized - Session expired or invalid')

        // Clear token from ApiClient
        this.clearToken()

        // Dispatch custom event for global auth state handling
        // This allows the app to respond to session expiration
        window.dispatchEvent(
          new CustomEvent('auth-expired', {
            detail: {
              message: 'Your session has expired. Please log in again.',
              statusCode: 401,
            },
          })
        )
      }
      throw error
    })

    // Error categorization and Sentry reporting interceptor
    this.addErrorInterceptor(error => {
      // Categorize error type
      let errorType: string
      let shouldCaptureInSentry = false

      if (error.isNetworkError) {
        errorType = 'network_error'
        shouldCaptureInSentry = true
        this.logger.error('Network Error:', error.message, error)
      } else if (error.statusCode && error.statusCode >= 500) {
        errorType = 'server_error'
        shouldCaptureInSentry = true
        this.logger.error('Server Error:', error.message, error)
      } else if (error.statusCode && error.statusCode >= 400) {
        errorType = 'client_error'
        // Client errors (4xx) are expected - don't capture in Sentry
        this.logger.warn('Client Error:', error.message, error)
      } else {
        errorType = 'unknown_error'
        shouldCaptureInSentry = true
        this.logger.error('Unknown Error:', error.message, error)
      }

      // Add Sentry breadcrumb for error
      Sentry.addBreadcrumb({
        category: 'http',
        message: `Error: ${error.message}`,
        level: 'error',
        data: {
          error_type: errorType,
          status_code: error.statusCode,
          is_network_error: error.isNetworkError,
          is_retryable: error.isRetryable,
        },
      })

      // Capture server errors (5xx) and network errors in Sentry
      if (shouldCaptureInSentry) {
        Sentry.withScope(scope => {
          // Add tags for error categorization
          scope.setTag('error_type', errorType)
          if (error.statusCode) {
            scope.setTag('http_status', String(error.statusCode))
          }
          scope.setTag('is_network_error', String(error.isNetworkError))
          scope.setTag('is_retryable', String(error.isRetryable))

          // Add context for debugging
          scope.setContext('api_error', {
            message: error.message,
            status_code: error.statusCode,
            is_network_error: error.isNetworkError,
            is_retryable: error.isRetryable,
            response_data: error.data,
          })

          // Capture the exception
          Sentry.captureException(error)
        })
      }

      throw error
    })
  }

  // Add interceptors
  public addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor)
  }

  public addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor)
  }

  public addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor)
  }

  // Clear interceptors
  public clearInterceptors(): void {
    this.requestInterceptors = []
    this.responseInterceptors = []
    this.errorInterceptors = []
    this.setupDefaultInterceptors()
  }

  private loadToken(): void {
    this.token = localStorage.getItem('auth_token')
  }

  private saveToken(token: string): void {
    if (!token || typeof token !== 'string') {
      this.logger.error('Invalid token provided to saveToken:', token)
      throw new Error('Invalid token provided')
    }

    this.token = token
    try {
      localStorage.setItem('auth_token', token)
    } catch (error) {
      this.logger.error('Failed to save token to localStorage:', error)
      // Still set the in-memory token even if localStorage fails
    }
  }

  private clearToken(): void {
    this.token = null
    localStorage.removeItem('auth_token')
  }

  // Retry logic with exponential backoff
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    retryCount: number = 0
  ): Promise<T> {
    try {
      return await requestFn()
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        error.isRetryable &&
        retryCount < this.maxRetries
      ) {
        const delay = this.retryDelay * Math.pow(2, retryCount)
        this.logger.log(
          `Retrying request in ${delay}ms (attempt ${retryCount + 1}/${this.maxRetries})`
        )
        await new Promise(resolve => setTimeout(resolve, delay))
        return this.retryRequest(requestFn, retryCount + 1)
      }
      throw error
    }
  }

  // Apply request interceptors
  private async applyRequestInterceptors(
    config: RequestConfig
  ): Promise<RequestConfig> {
    let currentConfig = config
    for (const interceptor of this.requestInterceptors) {
      currentConfig = await interceptor(currentConfig)
    }
    return currentConfig
  }

  // Apply response interceptors
  private async applyResponseInterceptors<T>(
    response: Response,
    data: T
  ): Promise<T> {
    let currentData = data
    for (const interceptor of this.responseInterceptors) {
      currentData = (await interceptor(response, currentData)) as T
    }
    return currentData
  }

  // Apply error interceptors
  private async applyErrorInterceptors(error: ApiClientError): Promise<never> {
    let currentError = error
    for (const interceptor of this.errorInterceptors) {
      try {
        await interceptor(currentError)
      } catch (err) {
        if (err instanceof ApiClientError) {
          currentError = err
        }
      }
    }
    throw currentError
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    return this.retryRequest(async () => {
      const url = `${this.baseUrl}${endpoint}`
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      }

      if (this.token) {
        headers.Authorization = `Bearer ${this.token}`
      }

      // Apply request interceptors
      const config = await this.applyRequestInterceptors({
        url,
        options: { ...options, headers },
      })

      try {
        const response = await fetch(config.url, config.options)

        let data: unknown
        const contentType = response.headers?.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          data = await response.json()
        } else {
          // Try JSON first, fallback to text
          try {
            data = await response.json()
          } catch {
            data = await response.text()
          }
        }

        if (!response.ok) {
          // Extract user-friendly error message from API response
          let errorMessage = `HTTP ${response.status}`
          if (typeof data === 'object' && data !== null && 'error' in data) {
            const errorData = data as Record<string, unknown>
            // Handle new error format: { error: { code: "...", message: "..." } }
            if (
              typeof errorData.error === 'object' &&
              errorData.error !== null &&
              'message' in errorData.error
            ) {
              errorMessage = (errorData.error as Record<string, unknown>)
                .message as string
            }
            // Handle legacy error format: { error: "..." }
            else if (typeof errorData.error === 'string') {
              errorMessage = errorData.error
            }
          }

          const isRetryable = this.retryableStatusCodes.has(response.status)

          throw new ApiClientError(errorMessage, {
            statusCode: response.status,
            response,
            data,
            isNetworkError: false,
            isRetryable,
          })
        }

        // Apply response interceptors
        return await this.applyResponseInterceptors(response, data as T)
      } catch (error) {
        // Convert to ApiClientError if it's a network error
        if (!(error instanceof ApiClientError)) {
          const networkError = new ApiClientError(
            error instanceof Error ? error.message : 'Network error',
            {
              isNetworkError: true,
              isRetryable: true,
            }
          )
          return this.applyErrorInterceptors(networkError)
        }

        return this.applyErrorInterceptors(error)
      }
    })
  }

  // Authentication methods
  async customerLogin(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>(
      '/v1/auth/customer/login',
      {
        method: 'POST',
        body: JSON.stringify(credentials),
      }
    )
    this.saveToken(response.token)
    return response
  }

  async customerRegister(data: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>(
      '/v1/auth/customer/register',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )

    // Handle email verification flow - registration returns message without token
    // The user must verify their email before they can login
    if (response.message && !response.token) {
      this.logger.log(
        'Registration requires email verification:',
        response.message
      )
      // Return response with message for UI to handle verification flow
      return {
        ...response,
        requires_verification: true,
      } as AuthResponse
    }

    // Legacy flow: if token is returned, save it (for backwards compatibility)
    if (response.token) {
      this.logger.log('Saving token from registration response')
      this.saveToken(response.token)
    }

    return response
  }

  async employeeLogin(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>(
      '/v1/auth/employee/login',
      {
        method: 'POST',
        body: JSON.stringify(credentials),
      }
    )
    this.saveToken(response.token)
    return response
  }

  async logout(): Promise<void> {
    await this.request('/v1/auth/logout', {
      method: 'POST',
    })
    this.clearToken()
  }

  async refreshToken(): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/v1/auth/refresh', {
      method: 'POST',
    })
    this.saveToken(response.token)
    return response
  }

  async getCurrentUser(): Promise<User> {
    // Check for dev mode mock user
    const devUser = localStorage.getItem('dev-user')
    if (devUser && import.meta.env.DEV) {
      return JSON.parse(devUser)
    }

    const response = await this.request<AuthResponse>('/v1/auth/me')
    return response.user
  }

  // Password management
  async changePassword(
    data: ChangePasswordRequest
  ): Promise<{ message: string; token?: string; user?: User }> {
    const response = await this.request<{
      message: string
      token?: string
      user?: User
    }>('/v1/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (response.token) {
      this.saveToken(response.token)
    }
    return response
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/health')
  }

  // Email verification methods
  async verifyEmail(token: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
    this.saveToken(response.token)
    return response
  }

  async resendVerification(
    email: string
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      '/v1/auth/resend-verification',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    )
  }

  // Utility methods
  isAuthenticated(): boolean {
    // Check for dev mode override
    if (import.meta.env.DEV && localStorage.getItem('dev-user')) {
      return true
    }

    return this.token !== null
  }

  getToken(): string | null {
    return this.token
  }

  // Typed HTTP method helpers
  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ): Promise<T> {
    let url = endpoint
    if (params) {
      const searchParams = new URLSearchParams()
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      }
      const queryString = searchParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }
    return this.request<T>(url, { method: 'GET' })
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  }

  async del<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    })
  }

  async getPaginated<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ): Promise<PaginatedResponse<T>> {
    return this.get<PaginatedResponse<T>>(endpoint, params)
  }

  async getChangelog(params?: {
    limit?: number
    cursor?: string
  }): Promise<PaginatedResponse<ChangelogEntry>> {
    return this.getPaginated<ChangelogEntry>('/v1/changelog', params)
  }

  async getCustomerDashboard(): Promise<CustomerDashboard> {
    const response = await this.get<{ data: CustomerDashboard }>(
      '/v1/customer/dashboard'
    )
    return response.data
  }

  async getAdminDashboard(): Promise<AdminDashboard> {
    const response = await this.get<{ data: AdminDashboard }>(
      '/v1/admin/dashboard'
    )
    return response.data
  }

  async getProviderDashboard(): Promise<ProviderDashboard> {
    const response = await this.get<{ data: ProviderDashboard }>(
      '/v1/provider/dashboard'
    )
    return response.data
  }

  async getCurrentGeofenceSettings(): Promise<GeofenceSettings> {
    const response = await this.get<{ data: GeofenceSettings }>(
      '/v1/admin/geofence/settings'
    )
    return response.data
  }

  async createGeofenceSettings(
    input: CreateGeofenceSettingsInput
  ): Promise<GeofenceSettings> {
    const response = await this.post<{ data: GeofenceSettings }>(
      '/v1/admin/geofence/settings',
      input
    )
    return response.data
  }

  async validateEmailDomain(email: string): Promise<EmailDomainValidation> {
    const response = await this.get<{ data: EmailDomainValidation }>(
      '/v1/validate-email',
      { email }
    )
    return response.data
  }
}

// Create default instance
export const apiClient = new ApiClient()
